import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/common/Seo";
import { HospitalCard, JourneyCard, StateCard } from "@/components/common/Cards";
import { SourceBadge } from "@/components/common/Badges";
import { OrganIcon, ORGAN_COLORS, OrganImage } from "@/components/common/OrganIcon";

export default function OrganPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setData(null); setErr(false);
    api.get(`/organs/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="max-w-7xl mx-auto px-6 py-24">Organ not found.</div>;
  if (!data) return <div className="max-w-7xl mx-auto px-6 py-24 text-neutral-500">Loading…</div>;

  const stateFilter = (params.get("state") || "").toUpperCase();
  const filterStateName = stateFilter
    ? (data.centres.find((c) => c.state_code === stateFilter)?.state_name || data.states.find((s) => s.code === stateFilter)?.name)
    : null;
  const centres = stateFilter ? data.centres.filter((c) => c.state_code === stateFilter) : data.centres;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <Seo title={`${data.name} Transplant in India — Transplant Story`} description={`${data.name} transplant centers and patient journeys across India.`} />
      <div className="text-[11px] font-mono uppercase tracking-widest text-rust mb-4">{data.name} transplant in {filterStateName || "India"}</div>
      <div className="flex items-center gap-5">
        <span data-testid="organ-page-icon" className="shrink-0 transition-transform duration-200 hover:scale-105">
          <OrganImage slug={data.slug} className="w-20 h-20 sm:w-24 sm:h-24" rounded="rounded-2xl" />
        </span>
        <h1 className="text-4xl sm:text-6xl font-light tracking-tight text-ink">{data.name}</h1>
      </div>

      <div className="grid grid-cols-3 gap-8 mt-12 max-w-xl">
        <Stat value={stateFilter ? centres.length : data.centre_count} label={stateFilter ? `Centers in ${filterStateName || "this state"}` : "Transplant centers"} />
        <Stat value={stateFilter ? data.journeys.filter((j) => j.state_code === stateFilter).length : data.journey_count} label="Patient journeys" />
        <Stat value={stateFilter ? 1 : data.state_count} label={stateFilter ? "State" : "States"} />
      </div>
      <div className="mt-5"><SourceBadge source={data.official_source.source} updated={data.official_source.updated} /></div>

      <div className="flex flex-wrap gap-3 mt-10">
        <Link to="/share" className="bg-ink text-white px-6 py-3 text-sm font-medium hover:bg-rust transition-colors">Share your {data.name.toLowerCase()} journey</Link>
      </div>

      {data.journeys.length > 0 && (
        <>
          <h2 className="text-2xl font-normal tracking-tight text-ink mt-20 mb-6">Patient journeys</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.journeys.map((j) => <JourneyCard key={j.id} journey={j} />)}
          </div>
        </>
      )}

      <div className="flex items-center gap-4 mt-20 mb-6 flex-wrap">
        <h2 className="text-2xl font-normal tracking-tight text-ink">
          {data.name} transplant centers {filterStateName ? `in ${filterStateName}` : "in India"}{" "}
          <span className="font-mono text-sm text-neutral-400">({centres.length})</span>
        </h2>
        {filterStateName && (
          <button data-testid="organ-clear-state-filter" onClick={() => navigate(`/organ/${slug}`)}
            className="text-xs font-mono text-rust border border-rust-border px-3 py-1.5 rounded-full hover:bg-rust-subtle transition-colors">
            × All India
          </button>
        )}
      </div>
      {centres.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-10 text-center text-neutral-500">
          No listed centers for {data.name.toLowerCase()} in this state yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {centres.map((c) => <HospitalCard key={c.id} centre={c} />)}
        </div>
      )}

      <h2 className="text-2xl font-normal tracking-tight text-ink mt-20 mb-6">States with {data.name.toLowerCase()} programs</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.states.map((s) => (
          <StateCard key={s.code} state={{
            ...s,
            centre_count: data.centres.filter((c) => c.state_code === s.code).length,
            journey_count: 0,
          }} />
        ))}
      </div>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="text-4xl sm:text-5xl font-light font-mono text-ink tabular-nums">{value}</div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mt-2">{label}</div>
    </div>
  );
}
